import * as Sentry from "@sentry/react";

// `__APP_VERSION__` is injected by Vite's `define` from package.json. See
// vite.config.ts. We fall back to "0.0.0-dev" if the build didn't inject it
// so a stray test importer doesn't throw at module load.
declare const __APP_VERSION__: string;
const APP_VERSION: string =
  typeof __APP_VERSION__ !== "undefined" ? __APP_VERSION__ : "0.0.0-dev";

let initialized = false;

// Initialize Sentry. Safe to call multiple times; only the first call takes
// effect. When `VITE_SENTRY_DSN` is empty (dev / local / E2E), we still call
// `Sentry.init` but install a `beforeSend: () => null` hook so nothing ever
// leaves the browser. That keeps the API surface identical across envs —
// callers can still invoke `Sentry.captureException` without checking whether
// Sentry is "on" — while guaranteeing a quiet dev loop.
export function initSentry(): void {
  if (initialized) return;
  initialized = true;

  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
  const mode = import.meta.env.MODE;
  const disabled = !dsn || dsn.trim() === "";

  Sentry.init({
    dsn: disabled ? undefined : dsn,
    environment: mode,
    release: APP_VERSION,
    tracesSampleRate: 0.1,
    // In local/dev without a DSN, short-circuit all events so we never spam
    // the free tier and never touch the network.
    beforeSend: disabled ? () => null : undefined,
  });
}

// Tag the current scope with the active organization so errors can be
// filtered by tenant in the Sentry UI. Pass `null` to clear on sign-out /
// org switch. The `OrgProvider` is expected to call this on org change —
// this module does not wire itself in so the provider can be updated in a
// later wave without churning this file.
export function tagOrganization(orgId: string | null): void {
  Sentry.setTag("organization_id", orgId ?? "");
}

// Re-export the bits the rest of the app might want so call sites don't need
// to import `@sentry/react` directly.
export { Sentry };
